import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { scroller } from "react-scroll";
import Sidebar from "../Nav/Sidebar";
import Backdrop from "../Elements/Backdrop";
import LogoIcon from "../../assets/svg/Logo";
import BurgerIcon from "../../assets/svg/BurgerIcon";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import LoginModal from "../../screens/Login";
import { doLogout } from "../../utils/auth";
import CartWidget from "../CartWidget";

const PUBLIC_SERVICES = [
  ["public-auctions", "Public Auctions"],
  ["debt-collections", "Debt Collections"],
  ["court-process-service", "Court Process Service"],
  ["repossessions-of-chattels-mortgage", "Repossessions of Chattels / Mortgage"],
  ["distress-of-rent", "Distress of Rent Service"],
  ["realisation-of-charged-securities", "Realisation of Charged Securities / Property Auctions"],
  ["private-investigations", "Private Investigations"],
  ["court-warrants-executions", "Court Warrants Executions"],
  ["other-services", "Other Services"],
];

export default function TopNavbar() {
  const [y, setY] = useState(window.scrollY);
  const [sidebarOpen, toggleSidebar] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [showLogin, setShowLogin] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  // "home mode" now covers the landing page plus the public properties
  // browse/detail pages, since they share the same top nav links
  const isHomePath = (path) => {
    return path === "/" || /^\/properties(\/|$)/.test(path) || /^\/services(\/|$)/.test(path) || path === "/about-us" || path === "/clients" || path === "/contact";
  };

  const [mode, setMode] = useState(() => (isHomePath(window.location.pathname) ? "home" : "admin"));
  const observerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const getAuthFromStorage = useCallback(() => {
    const raw = localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    const adminYn = localStorage.getItem("adminYn");
    const isAdmin = adminYn === "Y";
    return { user: u, isAdmin };
  }, []);

  const [{ user, isAdmin }, setAuth] = useState(() => getAuthFromStorage());

  useEffect(() => {
    const handleAuthEvent = () => setAuth(getAuthFromStorage());
    const onStorage = (e) => {
      if (["user", "adminYn", "token"].includes(e.key)) handleAuthEvent();
    };

    window.addEventListener("nava-login", handleAuthEvent);
    window.addEventListener("nava-logout", handleAuthEvent);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("nava-login", handleAuthEvent);
      window.removeEventListener("nava-logout", handleAuthEvent);
      window.removeEventListener("storage", onStorage);
    };
  }, [getAuthFromStorage]);

  const goToDashboard = () => {
    navigate("/admin");
  };

  const handleLogout = () => {
    doLogout(navigate);
    setAuth({ user: null, isAdmin: false });
    setMode("home");
  };


  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMode(isHomePath(location.pathname) ? "home" : "admin");
  }, [location.pathname]);

  const handleNavTo = (section) => {
    if (section === "properties") {
      navigate("/properties");
      return;
    }

    setActiveSection(section);
    if (location.pathname === "/") {
      scroller.scrollTo(section, {
        duration: 600,
        smooth: true,
        offset: -80,
      });
    } else {
      navigate("/", { state: { scrollTo: section } });
    }
  };

  useEffect(() => {
    if (location.pathname !== "/") {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const selectors = ["#home", "#projects", "#contact"];
    const elements = selectors.map((s) => document.querySelector(s)).filter(Boolean);
    if (elements.length === 0) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((en) => en.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const id = visible[0].target.id;
          setActiveSection(id);
        }
      },
      {
        root: null,
        threshold: [0.55, 0.6, 0.7],
      }
    );

    elements.forEach((el) => obs.observe(el));
    observerRef.current = obs;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      setActiveSection(target);
      if (location.pathname === "/") {
        scroller.scrollTo(target, {
          duration: 600,
          smooth: true,
          offset: -80,
        });
      }
    }
  }, [location.state, location.pathname]);

  const goHomeMode = () => {
    navigate("/");
    setMode("home");
  };

  return (
    <>
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <Backdrop toggleSidebar={toggleSidebar} />}
      <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
      <Wrapper className="flexCenter animate whiteBg" style={y > 100 ? { height: "60px" } : { height: "80px" }}>
        <NavInner className="container flexSpaceCenter">
          <LogoBtn onClick={() => { goHomeMode(); handleNavTo("home"); }} aria-label="Go to home">
            <div style={{ textAlign: "center" }}>
              <img
                src="/sare-logo.png"
                alt="Sare Auctioneers logo"
                style={{
                  width: 80,
                  height: 50,
                  objectFit: "contain",
                  display: "block",
                  margin: "0 auto",
                }}
                loading="lazy"
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontSize: "20px", fontWeight: "800", margin: 0, color: "#5d191a" }}>SARE</h1>
              <h2 style={{ fontSize: "14px", fontWeight: "800", margin: 0, color: "#8a3638" }}>AUCTIONEERS</h2>
            </div>
          </LogoBtn>

          <BurderWrapper className="pointer" onClick={() => toggleSidebar(!sidebarOpen)}>
            <BurgerIcon />
          </BurderWrapper>

          <UlWrapper className="flexNullCenter">
            {mode === "home" ? (
              <>
                <NavItem>
                  <NavButton onClick={() => { goHomeMode(); handleNavTo("home"); }} active={location.pathname === "/" && activeSection === "home"}>
                    Home
                  </NavButton>
                </NavItem>

                <ServiceNavItem
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                  onFocus={() => setServicesOpen(true)}
                >
                  <NavButton onClick={() => { setServicesOpen(false); navigate("/services"); }} active={location.pathname.startsWith("/services")} aria-haspopup="true" aria-expanded={servicesOpen}>
                    Services
                  </NavButton>
                  <ServicesMenu open={servicesOpen}>
                    {PUBLIC_SERVICES.map(([slug, label]) => (
                      <ServiceLink key={slug} type="button" onClick={() => { setServicesOpen(false); navigate(`/services/${slug}`); }}>
                        {label}
                      </ServiceLink>
                    ))}
                  </ServicesMenu>
                </ServiceNavItem>
                <NavItem>
                  <NavButton onClick={() => handleNavTo("properties")} active={location.pathname.startsWith("/properties")}>
                    Properties
                  </NavButton>
                </NavItem>

                <NavItem>
                  <NavButton onClick={() => navigate("/about-us")} active={location.pathname === "/about-us"}>
                    About Us
                  </NavButton>
                </NavItem>
                <NavItem>
                  <NavButton onClick={() => navigate("/clients")} active={location.pathname === "/clients"}>
                    Our Clients
                  </NavButton>
                </NavItem>
                <NavItem>
                  <NavButton onClick={() => navigate("/contact")} active={location.pathname === "/contact"}>
                    Contact
                  </NavButton>
                </NavItem>
              </>
            ) : (
              <>
                <NavItem>
                  <NavButton onClick={goHomeMode} active={false}>
                    Home
                  </NavButton>
                </NavItem>
                {isAdmin && (
                  <NavItem>
                    <NavButton onClick={goToDashboard} active={location.pathname === "/admin"}>
                      Dashboard
                    </NavButton>
                  </NavItem>
                )}
              </>
            )}
          </UlWrapper>

          <UlWrapperRight className="flexNullCenter">
            {isAdmin ? (
              <>
                <li className="semiBold font15 pointer">
                  <button
                    className="buttonLink"
                    onClick={goToDashboard}
                    style={{ padding: "10px 15px", border: 0, background: "transparent", cursor: "pointer" }}
                  >
                    Admin
                  </button>
                </li>
                <li className="semiBold font15 pointer">
                  <button
                    className="buttonLink"
                    onClick={handleLogout}
                    style={{ padding: "10px 15px", border: 0, background: "transparent", cursor: "pointer" }}
                  >
                    Log Out
                  </button>
                </li>
              </>
            ) : (
              <li className="semiBold font15 pointer">
                <button
                  className="buttonLink"
                  onClick={() => navigate("/admin/login")}
                  style={{ padding: "10px 15px", border: 0, background: "transparent", cursor: "pointer" }}
                >
                  Admin Log In
                </button>
              </li>
            )}
          </UlWrapperRight>

        </NavInner>

        <CartWidget />
      </Wrapper>
    </>
  );
}

const Wrapper = styled.nav`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
`;
const NavInner = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #3b1718;
`;

const BurderWrapper = styled.button`
  position: absolute;
  left: 12px;              
  top: 50%;
  transform: translateY(-50%);
  z-index: 10001;        
  outline: none;
  border: 0px;
  background-color: transparent;
  height: 40px;
  padding: 8px;
  display: none;

  @media (max-width: 760px) {
    display: block;
  }
`;

const LogoBtn = styled.button`
  background: transparent;
  border: 0;
  display: flex;
  align-items: center;
  cursor: pointer;

  @media (max-width: 760px) {
    margin-left: 48px;
  }
`;

const UlWrapper = styled.ul`
  display: flex;
  gap: 8px;
  list-style: none;
  align-items: center;
  margin: 0;
  padding: 0;
  @media (max-width: 760px) {
    display: none;
  }
`;

const UlWrapperRight = styled.ul`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  padding: 0;
  @media (max-width: 760px) {
    display: none;
  }
`;

const NavItem = styled.li`
  list-style: none;
`;

const ServiceNavItem = styled(NavItem)`
  position: relative;
`;

const ServicesMenu = styled.ul`
  display: ${(p) => (p.open ? "block" : "none")};
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  width: 290px;
  margin: 0;
  padding: 8px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #eadfe0;
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(59, 23, 24, 0.14);
`;

const ServiceLink = styled.button`
  display: block;
  width: 100%;
  padding: 9px 14px;
  border: 0;
  background: #fff;
  color: #5d191a;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    background: #f8eeee;
    outline: none;
  }
`;

const NavButton = styled.button`
  padding: 10px 15px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-weight: 700;
  color: #3b1718;
  position: relative;
  outline: none;

  &::after {
    content: "";
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: -8px;
    height: 3px;
    background: ${(p) => (p.active ? "#5d191a" : "transparent")};
    border-radius: 3px;
    transition: background 160ms ease;
  }

  &:hover::after {
    background: ${(p) => (p.active ? "#5d191a" : "rgba(93,25,26,0.12)")};
  }
`;
